import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, RefreshCw } from "lucide-react";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";
import { megaventoryPurchaseOrderDocumentUrl } from "@/lib/megaventoryDocumentUrls";
import { useManufacturingScopeHubWrite } from "@/lib/useHubModuleWrite";
import { cn, EM_DASH } from "@/lib/utils";

import {
  CLAMP_QC_CREDIT_REQUESTS_QUERY_KEY,
  createClampQcCreditRequest,
  fetchClampQcCreditRequests,
  updateClampQcCreditRequestStatus,
  type ClampQcCreditPurchaseOrderSummaryRow,
  type ClampQcCreditSubmittedFilter,
} from "./clampQcCreditRequestsApi";

const PAGE_SIZE = 50;

function formatDate(value: string | null): string {
  if (!value) return EM_DASH;
  const parsed = new Date(`${value.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function purchaseOrderLabel(value: string | null): string {
  return value?.trim() ? value.trim() : "No PO";
}

function purchaseOrderCell(row: ClampQcCreditPurchaseOrderSummaryRow): ReactNode {
  const label = purchaseOrderLabel(row.purchaseOrderNumber);
  if (row.purchaseOrderMvId != null && row.purchaseOrderNumber != null) {
    return (
      <a
        href={megaventoryPurchaseOrderDocumentUrl(row.purchaseOrderMvId, row.purchaseOrderNumber)}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 font-mono text-xs text-blue-700 underline decoration-blue-700/40 underline-offset-2 hover:text-blue-900"
        aria-label={`Open purchase order ${row.purchaseOrderNumber} in Megaventory`}
      >
        <span>{row.purchaseOrderNumber}</span>
        <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />
      </a>
    );
  }
  return <span className="font-mono text-xs text-slate-900">{label}</span>;
}

function creditRequestStatusLabel(
  status: ClampQcCreditPurchaseOrderSummaryRow["creditRequestStatus"],
): string {
  if (status === "draft") return "Draft";
  if (status === "submitted") return "Sent";
  if (status === "approved") return "Approved";
  return "No request";
}

export function ClampQcCreditRequestsPage() {
  const canWrite = useManufacturingScopeHubWrite();
  const queryClient = useQueryClient();

  const [submittedFilter, setSubmittedFilter] = useState<ClampQcCreditSubmittedFilter>("pending");
  const [purchaseOrderSearch, setPurchaseOrderSearch] = useState("");
  const [debouncedPurchaseOrder, setDebouncedPurchaseOrder] = useState("");
  const [subtypeFilter, setSubtypeFilter] = useState<"" | "large" | "small">("");
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedPurchaseOrder(purchaseOrderSearch.trim()), 300);
    return () => window.clearTimeout(handle);
  }, [purchaseOrderSearch]);

  useEffect(() => {
    setOffset(0);
  }, [submittedFilter, debouncedPurchaseOrder, subtypeFilter]);

  const listQuery = useQuery({
    queryKey: [
      ...CLAMP_QC_CREDIT_REQUESTS_QUERY_KEY,
      submittedFilter,
      debouncedPurchaseOrder,
      subtypeFilter,
      offset,
    ],
    queryFn: () =>
      fetchClampQcCreditRequests({
        submitted: submittedFilter,
        purchaseOrder: debouncedPurchaseOrder,
        subtype: subtypeFilter,
        limit: PAGE_SIZE,
        offset,
      }),
  });

  const actionMutation = useMutation({
    mutationFn: async (input: {
      kind: "create" | "submit" | "approve";
      row: ClampQcCreditPurchaseOrderSummaryRow;
    }) => {
      if (input.kind === "create") {
        if (input.row.purchaseOrderNumber == null) {
          throw new Error("Cannot create a credit request without a purchase order.");
        }
        return createClampQcCreditRequest(input.row.purchaseOrderNumber);
      }
      if (input.row.creditRequestId == null) {
        throw new Error("No credit request exists for this purchase order.");
      }
      if (input.kind === "submit") {
        return updateClampQcCreditRequestStatus(input.row.creditRequestId, "submitted");
      }
      return updateClampQcCreditRequestStatus(input.row.creditRequestId, "approved");
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CLAMP_QC_CREDIT_REQUESTS_QUERY_KEY });
    },
  });

  const rows = listQuery.data?.rows ?? [];
  const totalCount = listQuery.data?.totalCount ?? 0;
  const summary = listQuery.data?.summary;
  const pageStart = totalCount === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + rows.length, totalCount);
  const hasPreviousPage = offset > 0;
  const hasNextPage = offset + PAGE_SIZE < totalCount;

  const listError =
    listQuery.error instanceof ApiError
      ? listQuery.error.message
      : listQuery.error instanceof Error
        ? listQuery.error.message
        : null;

  const actionError =
    actionMutation.error instanceof ApiError
      ? actionMutation.error.message
      : actionMutation.error instanceof Error
        ? actionMutation.error.message
        : null;

  const filtersActive =
    debouncedPurchaseOrder !== "" || subtypeFilter !== "" || submittedFilter !== "pending";

  const creditColumns = useMemo((): HubDataTableColumn<ClampQcCreditPurchaseOrderSummaryRow>[] => {
    const base: HubDataTableColumn<ClampQcCreditPurchaseOrderSummaryRow>[] = [
      {
        id: "purchaseOrder",
        header: "PO",
        sortKey: "purchaseOrder",
        sortValue: (row) => row.purchaseOrderNumber ?? "",
        cell: (row) => purchaseOrderCell(row),
      },
      {
        id: "received",
        header: "Received",
        sortKey: "received",
        overflow: "nowrap",
        sortValue: (row) => row.gipiReceivedDate ?? "",
        cell: (row) => formatDate(row.gipiReceivedDate),
      },
      {
        id: "status",
        header: "Status",
        sortKey: "status",
        sortValue: (row) => row.purchaseOrderStatus ?? "",
        cell: (row) => row.purchaseOrderStatus ?? EM_DASH,
      },
      {
        id: "smallCount",
        header: "Small",
        sortKey: "smallCount",
        align: "right",
        sortValue: (row) => row.smallCount,
        cell: (row) => row.smallCount.toLocaleString(),
      },
      {
        id: "largeCount",
        header: "Large",
        sortKey: "largeCount",
        align: "right",
        sortValue: (row) => row.largeCount,
        cell: (row) => row.largeCount.toLocaleString(),
      },
      {
        id: "pendingCount",
        header: "Not sent",
        sortKey: "pendingCount",
        align: "right",
        sortValue: (row) => row.pendingCount,
        cell: (row) => row.pendingCount.toLocaleString(),
      },
      {
        id: "submittedCount",
        header: "Sent",
        sortKey: "submittedCount",
        align: "right",
        sortValue: (row) => row.submittedCount,
        cell: (row) => row.submittedCount.toLocaleString(),
      },
      {
        id: "totalCount",
        header: "Total failed",
        sortKey: "totalCount",
        align: "right",
        sortValue: (row) => row.totalCount,
        cell: (row) => <span className="font-medium">{row.totalCount.toLocaleString()}</span>,
      },
      {
        id: "creditRequest",
        header: "Credit request",
        sortKey: "creditRequest",
        sortValue: (row) => row.creditRequestStatus ?? "",
        cell: (row) => (
          <Badge tone={row.creditRequestStatus === "approved" ? "success" : "neutral"}>
            {creditRequestStatusLabel(row.creditRequestStatus)}
          </Badge>
        ),
      },
    ];
    if (!canWrite) {
      return base;
    }
    return [
      ...base,
      {
        id: "action",
        header: "Action",
        sortValue: () => "",
        cell: (row) => {
          const hasPo = row.purchaseOrderNumber != null;
          const showCreate = hasPo && row.creditRequestStatus == null;
          const showMarkSent = row.creditRequestStatus === "draft";
          const showMarkApproved = row.creditRequestStatus === "submitted";
          return (
            <div className="flex flex-col gap-1">
              {showCreate ? (
                <Button
                  type="button"
                  size="sm"
                  variant="primary"
                  disabled={actionMutation.isPending}
                  onClick={() => actionMutation.mutate({ kind: "create", row })}
                >
                  Create credit request
                </Button>
              ) : null}
              {showMarkSent ? (
                <Button
                  type="button"
                  size="sm"
                  variant="primary"
                  disabled={actionMutation.isPending}
                  onClick={() => actionMutation.mutate({ kind: "submit", row })}
                >
                  Mark sent
                </Button>
              ) : null}
              {showMarkApproved ? (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={actionMutation.isPending}
                  onClick={() => actionMutation.mutate({ kind: "approve", row })}
                >
                  Mark approved
                </Button>
              ) : null}
            </div>
          );
        },
      },
    ];
  }, [actionMutation, canWrite]);

  return (
    <div className="space-y-4 pb-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp hoverHint="Scrapped clamp QC failures grouped by supplier GPO. Received date is the earliest non-cancelled GIPI (purchase invoice) on that GPO. Status is hub lifecycle for Hub-owned purchase orders, otherwise Megaventory status. Create a credit request per GPO, mark it sent when submitted to the supplier, then approved when credit is confirmed.">
          Clamp QC credit
        </PageTitleWithHelp>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => void listQuery.refetch()}
          disabled={listQuery.isFetching}
        >
          <RefreshCw className={cn("h-4 w-4", listQuery.isFetching && "animate-spin")} aria-hidden />
          Refresh
        </Button>
      </div>

      {summary ? (
        <div className="flex flex-wrap gap-2 text-sm text-slate-600">
          <Badge tone="warning">
            {summary.pendingCount.toLocaleString()} failed serials not yet sent for credit
          </Badge>
          <Badge tone="success">
            {summary.submittedCount.toLocaleString()} failed serials already sent for credit
          </Badge>
        </div>
      ) : null}

      {actionError ? <p className="text-sm text-red-700">{actionError}</p> : null}

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-3 md:grid-cols-3">
          <div className="space-y-1 md:col-span-2">
            <Label htmlFor="clamp-credit-po-search">Search PO</Label>
            <Input
              id="clamp-credit-po-search"
              type="search"
              value={purchaseOrderSearch}
              onChange={(event) => setPurchaseOrderSearch(event.target.value)}
              placeholder="Filter by PO number…"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="clamp-credit-subtype">Clamp size</Label>
            <select
              id="clamp-credit-subtype"
              className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
              value={subtypeFilter}
              onChange={(event) =>
                setSubtypeFilter(event.target.value === "large" || event.target.value === "small" ? event.target.value : "")
              }
            >
              <option value="">All sizes</option>
              <option value="small">Small</option>
              <option value="large">Large</option>
            </select>
          </div>
          <div className="space-y-2 md:col-span-3">
            <Label>Failed serials</Label>
            <div className="flex flex-wrap gap-2">
              {(
                [
                  { key: "pending" as const, label: "Not sent" },
                  { key: "submitted" as const, label: "Sent" },
                  { key: "all" as const, label: "All" },
                ] as const
              ).map((option) => (
                <Button
                  key={option.key}
                  type="button"
                  size="sm"
                  variant={submittedFilter === option.key ? "primary" : "secondary"}
                  onClick={() => setSubmittedFilter(option.key)}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </div>
        </CardBody>
      </Card>

      <HubDataTable
        title={
          listQuery.isLoading
            ? "Loading clamp QC credit summary…"
            : totalCount === 0
              ? "No matching purchase orders"
              : `Showing ${pageStart.toLocaleString()}–${pageEnd.toLocaleString()} of ${totalCount.toLocaleString()} purchase orders`
        }
        headerExtra={
          totalCount > PAGE_SIZE ? (
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={!hasPreviousPage}
                onClick={() => setOffset((value) => Math.max(0, value - PAGE_SIZE))}
              >
                Previous
              </Button>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={!hasNextPage}
                onClick={() => setOffset((value) => value + PAGE_SIZE)}
              >
                Next
              </Button>
            </div>
          ) : null
        }
        columns={creditColumns}
        rows={rows}
        rowKey={(row) => row.purchaseOrderNumber ?? "__none__"}
        loading={listQuery.isLoading}
        loadingMessage="Loading clamp QC credit summary…"
        error={listError}
        empty="No purchase orders match these filters."
        isFiltered={filtersActive}
        filteredEmpty="No purchase orders match these filters."
      />
    </div>
  );
}
